<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { Icon } from '@oat-sa-private/ui-elements';
    import CheckMark from './CheckMark.svelte';
    import Stepper from './Stepper.svelte';
    import StepperArray from './StepperArray.svelte';

    /**
     * @typedef {Object} Props
     * @property {String} icon
     * @property {String} label
     * @property {Number} [min] - Stepper prop
     * @property {Number} [step] - Stepper prop
     * @property {Number} [max] - Stepper prop
     * @property {Number} [value] - Stepper prop
     * @property {String} ariaLabelDecr - Stepper prop
     * @property {String} ariaLabelIncr - Stepper prop
     * @property {Array} [options]
     * @property {Boolean} [nonDefault]
     * @property {Function} onChange
     */
    /** @type {Props} */
    let {
        icon,
        label,
        min = 0,
        step = 1,
        max = 10,
        value = 0,
        ariaLabelDecr,
        ariaLabelIncr,
        options = [],
        nonDefault = $bindable(false),
        onChange
    } = $props();

    /**
     * Read and propagate Stepper change event
     * @param {Object} [newState]
     */
    function handleChange(newState = {}) {
        nonDefault = newState?.nonDefault;

        onChange?.(newState);
    }
</script>

<style>
    .control-row {
        display: flex;
        justify-content: flex-between;
        align-items: center;

        &.non-default {
            color: var(--color-brand);
        }
        & > :global(.icon) {
            margin-inline-end: 1rem;
        }
        & label {
            font-size: var(--fontsize-body-s);
            flex-grow: 1;
        }
    }
</style>

<span class="control-row" class:non-default={nonDefault}>
    {#if icon}
        <Icon name={icon} ariaHidden />
    {/if}
    <label>
        {label || ''}
        {#if nonDefault}
            <CheckMark />
        {/if}
    </label>
    {#if options.length === 0}
        <Stepper {min} {max} {step} {value} {ariaLabelDecr} {ariaLabelIncr} onChange={handleChange} />
    {:else}
        <StepperArray {options} {value} {ariaLabelDecr} {ariaLabelIncr} onChange={handleChange} />
    {/if}
</span>
